import { nextTick } from 'vue';
import { START_LOCATION } from 'vue-router';
import { IGNORED_LEGACY_VIEWS, buildAutoloadValue, decideHistoryMode, legacyPayloadForRoute, readLegacySessionUserId, routeLocationForLegacyEvent } from './legacyViewMap';
import { legacyNav } from '@/iframe/legacyNavState';

// Keeps Vue Router and the legacy in-app navigation (amplify 'evtChangeView') in sync, both ways:
//   legacy → route: iframeWatcher's evtChangeView subscriber calls handleLegacyViewChange()
//   route → legacy: router.afterEach publishes the matched route's legacy view
//   legacy portal ↔ '/': iframeWatcher feeds handleLegacyPageChange() with the legacy hasher page
// No window / document access at module scope and every side effect goes through `deps`, so the
// whole state machine runs under the node vitest environment.

export const AUTOLOAD_COOKIE = 'AUTOLOAD';
export const AUTOLOAD_SEED_KEY = 'vueAutoloadSeed';
const AUTOLOAD_MAX_AGE_S = 600;
const HOME_VIEW = 'widgets';
// Same delay the legacy uses between its greeter reset and the real view (visitPreview.js, pos.js).
const LEGACY_RESET_DELAY_MS = 2500;
const PAYLOAD_MEMORY_LIMIT = 30;
const BREAKER_WINDOW_MS = 100;
const BREAKER_MAX_PUBLISHES = 10;
const BREAKER_COOLDOWN_MS = 1000;
const IN_FLIGHT_TIMEOUT_MS = 1500;
// Same retry delay as the removed sidebar.js changeView(); ~15s, the menus load right after the first sync.
const MENU_WAIT_MS = 750;
const MENU_WAIT_MAX_ATTEMPTS = 20;
// Set by the Vue SSO handler (cookieUtils.setSSOCookies) and consumed by the legacy portal (home.js clearCook),
// which then opens the requested application itself.
const SSO_HANDOFF_COOKIE = '__a';
// The portal lists its applications once Core/Applications answers: ~20s.
const PORTAL_ENTRY_RETRY_MS = 250;
const PORTAL_ENTRY_MAX_ATTEMPTS = 80;
// home.js clickHandler waits for the three application logons, without any error path.
const PORTAL_ENTRY_TIMEOUT_MS = 30000;
// A Back traversal changes this URL and the iframe hash in separate tasks.
const PORTAL_SETTLE_MS = 500;

const readBrowserCookie = (name) => {
	const prefix = `${name}=`;
	const raw = document.cookie
		.split(';')
		.map((c) => c.trim())
		.find((c) => c.startsWith(prefix));
	if (!raw) return null;
	try {
		return atob(raw.substring(prefix.length)) || null;
	} catch {
		return null;
	}
};

const defaultDeps = {
	getIframeWindow: () => document.querySelector('iframe')?.contentWindow ?? null,
	unmountAll: () => {},
	log: () => {},
	now: () => Date.now(),
	isEmbedded: () => window.top !== window,
	// Same encoding and attributes as the legacy cookieHelper (bare btoa, path=/, SameSite=None; Secure).
	readCookie: readBrowserCookie,
	writeCookie: (name, value, maxAgeSeconds) => {
		document.cookie = `${name}=${btoa(value)}; max-age=${maxAgeSeconds}; path=/; SameSite=None; Secure`;
	},
	deleteCookie: (name) => {
		document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/`;
	},
	getSessionStorage: () => window.sessionStorage,
	getLocalStorage: () => window.localStorage,
	schedule: (fn, ms) => setTimeout(fn, ms),
	cancel: (id) => clearTimeout(id),
	appointmentType: () => 2,
	// Clicks the legacy portal's eCOS tile; false while its application list is not there.
	enterApplication: () => false,
	// Brings the legacy back to its portal (c_reloadWebApp, a full reload).
	openPortal: () => {},
};

let router = null;
let deps = defaultDeps;

// Loop prevention, one mechanism per direction:
//   route → legacy → route: our publish comes straight back through iframeWatcher (amplify is
//   synchronous), but afterEach only runs once the route is committed, so the echoed view resolves
//   to the current fullPath and handleLegacyViewChange stops there.
//   legacy → route → legacy: `inFlight` marks the navigation we started so its afterEach does not
//   publish the view back to the legacy that just announced it.
let inFlight = null;
let inFlightTimer = null;

let legacyReady = false;
let pendingPayload = null;
let bootTarget = null;
let resetTimer = null;
let menuWaitTimer = null;
let lastLegacyNavAt = -Infinity;
let iframeLoadCount = 0;
let publishTimes = [];
let breakerOpenUntil = 0;
const payloadMemory = new Map();

// Legacy page of the current document (legacyNavState: 'portal', 'login', 'app'), and what decides whether
// landing on the portal opens the application: only a boot that restored a session does (see handleLegacyPageChange).
let legacyPage = null;
let ssoHandoff = false;
let loginSeen = false;
let portalEntryTried = false;
let portalEntryTimer = null;
let portalCheckTimer = null;

const safe = (fn, fallback = null) => {
	try {
		return fn();
	} catch {
		return fallback;
	}
};

function getLegacy() {
	const win = safe(() => deps.getIframeWindow());
	const externis = safe(() => win?.externis);
	if (!externis || !safe(() => win.amplify)) return null;
	return { amplify: win.amplify, ecosVM: externis.ecosVM };
}

function clearInFlight() {
	inFlight = null;
	if (inFlightTimer) {
		deps.cancel(inFlightTimer);
		inFlightTimer = null;
	}
}

function cancelPendingReset() {
	if (resetTimer) {
		deps.cancel(resetTimer);
		resetTimer = null;
	}
	if (menuWaitTimer) {
		deps.cancel(menuWaitTimer);
		menuWaitTimer = null;
	}
}

function rememberPayload(path, evt) {
	payloadMemory.delete(path);
	payloadMemory.set(path, { appMenu: evt.appMenu, view: evt.view, label: evt.label, data: evt.data ?? null });
	if (payloadMemory.size > PAYLOAD_MEMORY_LIMIT) payloadMemory.delete(payloadMemory.keys().next().value);
}

function breakerTripped() {
	const t = deps.now();
	if (t < breakerOpenUntil) return true;

	publishTimes = publishTimes.filter((time) => t - time < BREAKER_WINDOW_MS);
	publishTimes.push(t);
	if (publishTimes.length <= BREAKER_MAX_PUBLISHES) return false;

	breakerOpenUntil = t + BREAKER_COOLDOWN_MS;
	publishTimes = [];
	deps.log('error', 'router', `evtChangeView publish storm (>${BREAKER_MAX_PUBLISHES} in ${BREAKER_WINDOW_MS}ms) — route→legacy sync paused ${BREAKER_COOLDOWN_MS}ms`);
	return true;
}

// The removed sidebar.js changeView() re-queued itself every 750ms while the payload's menu list was
// empty, and never stopped when the user simply had no entry in that menu (customers without SFA
// reach vwDocObjectives / vwWatch under menuSFA). It did nothing beyond that wait plus
// `amplify.publish('evtChangeView', …)` - the view context (ctx.app, body classes, traceView, header)
// is applied by eagle.js's own evtChangeView subscriber, whoever publishes. The bridge runs the same
// wait, bounded, sourced from Vue's own menu build (legacyNavState.js, from Core/Menus), then publishes.
const MENU_GROUPS = { menuSFA: 'sfa', menuSALES: 'sales', menuDOC: 'doc' };

function shouldWaitForMenu(payload) {
	if (payload.view === 'greeter') return false;
	const key = MENU_GROUPS[payload.appMenu];
	if (!key) return false;
	if ((legacyNav.menus[key] ?? []).length > 0) return false;
	return !legacyNav.menusReady;
}

function publish(payload, attempt = 0) {
	const legacy = getLegacy();
	if (!legacy) {
		pendingPayload = payload;
		return;
	}

	if (shouldWaitForMenu(payload) && attempt < MENU_WAIT_MAX_ATTEMPTS) {
		menuWaitTimer = deps.schedule(() => {
			menuWaitTimer = null;
			publish(payload, attempt + 1);
		}, MENU_WAIT_MS);
		return;
	}
	if (breakerTripped()) return;

	try {
		legacy.amplify.publish('evtChangeView', payload);
	} catch (error) {
		deps.log('error', 'router', `Legacy navigation to ${payload.view} failed: ${error.message}`);
	}
}

function requestLegacyNavigation(payload) {
	if (!payload) return;
	cancelPendingReset();

	if (!legacyReady || !getLegacy()) {
		pendingPayload = payload;
		return;
	}
	pendingPayload = null;

	// The legacy only rebinds a component when currentView changes, so reaching the same view with
	// other data (/visit/A → /visit/B) goes through the same greeter reset the legacy uses itself.
	const currentView = safe(() => getLegacy()?.ecosVM?.currentView?.());
	if (currentView === payload.view) {
		publish({ view: 'greeter' });
		resetTimer = deps.schedule(() => {
			resetTimer = null;
			publish(payload);
		}, LEGACY_RESET_DELAY_MS);
		return;
	}

	publish(payload);
}

function payloadForRoute(route) {
	return legacyPayloadForRoute(route, payloadMemory.get(route.fullPath), { appointmentType: safe(() => deps.appointmentType()) });
}

const onPortalRoute = () => router.currentRoute.value.meta?.legacyPage === 'portal';

function cancelPortalEntry() {
	if (portalEntryTimer) {
		deps.cancel(portalEntryTimer);
		portalEntryTimer = null;
	}
}

function cancelPortalCheck() {
	if (portalCheckTimer) {
		deps.cancel(portalCheckTimer);
		portalCheckTimer = null;
	}
}

// The legacy shows its portal: so does the URL. Replace, the iframe hash change already added the history entry.
function followLegacyToPortal() {
	cancelPortalEntry();
	if (onPortalRoute()) return;

	const target = router.resolve({ name: 'Portal' });
	clearInFlight();
	inFlight = { path: target.fullPath };
	router
		.replace(target.fullPath)
		.catch(() => {})
		.finally(() => {
			if (inFlight?.path === target.fullPath) clearInFlight();
		});
}

// Opening the application is the portal's own eCOS tile (home.js clickHandler): it waits for the
// application logons and the sales options, which set externis.sfaActive (gates the SFA synchronization)
// and the other globals only the portal sets. Never boot the legacy straight into #ecos instead.
function enterApplicationFromPortal(attempt = 0) {
	portalEntryTimer = null;
	if (safe(() => deps.enterApplication(), false)) {
		portalEntryTimer = deps.schedule(() => {
			portalEntryTimer = null;
			if (legacyPage !== 'portal') return;
			deps.log('warn', 'router', 'The legacy portal did not open the application — staying on the portal');
			followLegacyToPortal();
		}, PORTAL_ENTRY_TIMEOUT_MS);
		return;
	}

	if (attempt < PORTAL_ENTRY_MAX_ATTEMPTS) {
		portalEntryTimer = deps.schedule(() => enterApplicationFromPortal(attempt + 1), PORTAL_ENTRY_RETRY_MS);
		return;
	}
	deps.log('warn', 'router', 'No eCOS application on the legacy portal — staying on the portal');
	followLegacyToPortal();
}

// A boot that restored a session lands on the portal (Startup.js checkRedirect, hasherPath '/'): the URL
// asked for the application, so it is opened. After the login page (the user lands on the portal), an SSO
// handoff (home.js opens the requested application itself), inside the BO frameset or once the application
// has been shown in this document, the URL follows the legacy onto the portal instead.
const shouldEnterApplication = () => !portalEntryTried && !legacyReady && !loginSeen && !ssoHandoff && !safe(() => deps.isEmbedded(), false);

// Fed by iframeWatcher with legacyNavState's page (from the legacy hasher route), login state and hidden BO app.
export function handleLegacyPageChange({ page = null, loggedIn = false, hiddenApp = false } = {}) {
	if (!router) return;
	legacyPage = page;
	if (page === 'login') loginSeen = true;

	if (page !== 'portal' || !loggedIn || hiddenApp) {
		cancelPortalEntry();
		return;
	}
	if (onPortalRoute() || portalEntryTimer) return;

	if (shouldEnterApplication()) {
		portalEntryTried = true;
		enterApplicationFromPortal();
		return;
	}
	followLegacyToPortal();
}

// The Vue portal reads the legacy portal's state: an in-app navigation to it while the legacy shows the
// application brings the legacy back there, like the portal button. A Back traversal usually restores the
// iframe's portal hash on its own, hence the delay.
function schedulePortalCheck() {
	cancelPortalCheck();
	portalCheckTimer = deps.schedule(() => {
		portalCheckTimer = null;
		if (onPortalRoute() && legacyPage === 'app') safe(() => deps.openPortal());
	}, PORTAL_SETTLE_MS);
}

async function onAfterEach(to, from, failure) {
	if (failure) return;

	if (inFlight && inFlight.path === to.fullPath) {
		clearInFlight();
		return;
	}

	// Initial navigation: the legacy boots its own first view; a deep link reaches it through the
	// AUTOLOAD cookie written by seedLegacyBootNavigation(), never through an early publish.
	if (from === START_LOCATION) return;

	if (to.meta?.legacyPage === 'portal') {
		if (legacyReady) safe(() => deps.unmountAll());
		schedulePortalCheck();
		return;
	}

	if (!to.meta?.legacyView) {
		// Native Vue page: nothing is published, so legacy zones would otherwise keep running behind the hidden iframe.
		if (legacyReady) safe(() => deps.unmountAll());
		return;
	}

	await nextTick();
	requestLegacyNavigation(payloadForRoute(to));
}

function readSession(key) {
	return safe(() => deps.getSessionStorage()?.getItem(key));
}

function releaseOwnAutoload() {
	const own = readSession(AUTOLOAD_SEED_KEY);
	if (!own) return;
	safe(() => deps.getSessionStorage()?.removeItem(AUTOLOAD_SEED_KEY));
	if (safe(() => deps.readCookie(AUTOLOAD_COOKIE)) === own) safe(() => deps.deleteCookie(AUTOLOAD_COOKIE));
}

// Entry point of the Vue chrome menus. The URL still drives the legacy: the menu payload (appMenu,
// label, data) is recorded for the target URL, and afterEach publishes it once the route is committed.
export function navigateToLegacyView(payload) {
	if (!router || !payload?.view) return false;

	const location = routeLocationForLegacyEvent(payload);
	if (!location) {
		deps.log('warn', 'router', `No route for legacy view "${payload.view}" — published without a URL change`);
		requestLegacyNavigation(payload);
		return false;
	}

	const target = router.resolve(location);
	rememberPayload(target.fullPath, payload);

	if (target.fullPath === router.currentRoute.value.fullPath) {
		// Like the legacy menus, re-selecting the displayed view does nothing.
		if (safe(() => getLegacy()?.ecosVM?.currentView?.()) !== payload.view) requestLegacyNavigation(payloadForRoute(target));
		return true;
	}

	router.push(target.fullPath).catch(() => {});
	return true;
}

export function installLegacyNavigationBridge(appRouter, overrides = {}) {
	router = appRouter;
	deps = { ...defaultDeps, ...overrides };
	router.afterEach(onAfterEach);
}

// Called before the legacy boots (main.js, before app.mount) and again on an iframe-only reload.
// The legacy always lands on its home view unless an AUTOLOAD cookie is present (eagle.js
// evtAppSwitching / replayAutoload, formerly sidebar.js), which is the proven path home.js already
// uses for BO links. It stays legacy-driven on purpose: the boot evtAppSwitching can fire before
// this window has subscribed to the freshly loaded document's amplify.
export function seedLegacyBootNavigation(path) {
	if (!router) return false;

	ssoHandoff = Boolean(safe(() => deps.readCookie(SSO_HANDOFF_COOKIE)));
	releaseOwnAutoload();
	bootTarget = null;

	if (safe(() => deps.isEmbedded(), false)) return false;

	const target = router.resolve(path);
	const value = buildAutoloadValue(target);
	if (!value) return false;

	// A reload after logout has no legacy session: the URL belongs to whoever used the app before.
	if (!readLegacySessionUserId(safe(() => deps.getLocalStorage()))) return false;

	// A deep link written by the legacy app or the BO wins over the current URL.
	if (safe(() => deps.readCookie(AUTOLOAD_COOKIE))) return false;

	safe(() => deps.writeCookie(AUTOLOAD_COOKIE, value, AUTOLOAD_MAX_AGE_S));
	safe(() => deps.getSessionStorage()?.setItem(AUTOLOAD_SEED_KEY, value));
	bootTarget = target;
	return true;
}

// js/Main.js protectMobilePreventBack(), called by context.js setHashSilently() on every legacy
// view change, pushes a history entry and sets onpopstate = () => history.go(1): Back is cancelled
// for the whole browsing context (the iframe shares session history with this window).
export function neutralizeLegacyHistoryTrap(win) {
	if (!win) return false;
	try {
		win.protectMobilePreventBack = () => {};
		win.onpopstate = null;
		return true;
	} catch (error) {
		deps.log('warn', 'router', `Could not neutralize the legacy Back trap: ${error.message}`);
		return false;
	}
}

// Called from iframeWatcher's iframe.onload, before anything else: each load is a fresh legacy document.
export function onLegacyIframeLoad(win) {
	iframeLoadCount += 1;
	neutralizeLegacyHistoryTrap(win);

	legacyReady = false;
	lastLegacyNavAt = -Infinity;
	cancelPendingReset();
	cancelPortalEntry();
	cancelPortalCheck();
	legacyPage = null;
	loginSeen = false;
	portalEntryTried = false;
	// Recorded payloads hold live objects of the previous document.
	payloadMemory.clear();

	if (iframeLoadCount > 1 && router) seedLegacyBootNavigation(router.currentRoute.value.fullPath);
}

export function handleLegacyViewChange(evt) {
	if (!router) return;

	const view = evt?.view;
	if (!view || IGNORED_LEGACY_VIEWS.has(view)) return;

	const firstCommit = !legacyReady;
	legacyReady = true;

	if (firstCommit && pendingPayload) {
		// Vue asked for a view before the legacy was ready: that newer intent wins over the boot view.
		const payload = pendingPayload;
		pendingPayload = null;
		bootTarget = null;
		deps.schedule(() => requestLegacyNavigation(payload), 0);
		return;
	}

	if (firstCommit && bootTarget) {
		const target = bootTarget;
		bootTarget = null;
		releaseOwnAutoload();
		if (view !== target.meta.legacyView && view === HOME_VIEW) {
			// AUTOLOAD was not honoured and the legacy fell back to its home: replay the deep link once,
			// after the current evtChangeView dispatch has finished. Any other view is a deliberate
			// legacy redirect (e.g. pending offline orders) and the URL follows it below.
			deps.schedule(() => requestLegacyNavigation(payloadForRoute(target)), 0);
			return;
		}
	}

	const location = routeLocationForLegacyEvent(evt);
	if (!location) {
		deps.log('warn', 'router', `No route for legacy view "${view}" — URL left unchanged`);
		return;
	}

	const target = router.resolve(location);
	rememberPayload(target.fullPath, evt);

	// Also stamped for our own echoed publishes: a legacy redirect fired right after a Vue-initiated
	// navigation (the target view bouncing elsewhere) then replaces that entry instead of stacking one
	// that Back would bounce off again.
	const now = deps.now();
	const sinceLastLegacyNav = now - lastLegacyNavAt;
	lastLegacyNavAt = now;

	if (target.fullPath === router.currentRoute.value.fullPath) return;

	const mode = decideHistoryMode({
		embedded: safe(() => deps.isEmbedded(), false),
		firstCommit,
		fromPortal: onPortalRoute(),
		state: evt.state ?? null,
		routeHistoryMode: target.meta?.historyMode ?? null,
		sinceLastLegacyNav,
		previousPath: safe(() => router.options.history.state?.back),
		targetPath: target.fullPath,
	});

	clearInFlight();
	inFlight = { path: target.fullPath };

	if (mode === 'back') {
		router.back();
		inFlightTimer = deps.schedule(clearInFlight, IN_FLIGHT_TIMEOUT_MS);
		return;
	}

	router[mode](target.fullPath)
		.catch(() => {})
		.finally(() => {
			if (inFlight?.path === target.fullPath) clearInFlight();
		});
}

export function __resetLegacyNavigationBridgeForTests() {
	router = null;
	deps = defaultDeps;
	inFlight = null;
	inFlightTimer = null;
	legacyReady = false;
	pendingPayload = null;
	bootTarget = null;
	resetTimer = null;
	menuWaitTimer = null;
	lastLegacyNavAt = -Infinity;
	iframeLoadCount = 0;
	publishTimes = [];
	breakerOpenUntil = 0;
	payloadMemory.clear();
	legacyPage = null;
	ssoHandoff = false;
	loginSeen = false;
	portalEntryTried = false;
	portalEntryTimer = null;
	portalCheckTimer = null;
}
